<template>
  <!-- Kit ToolbarSearch. Variants: home (30 tall, max 460, centred), pane
       (340 x 30), list (28) and phone (36, the phone boards' field). With a
       hint (⌘K or ⌘F) it registers its input with shell/shortcuts.js, and
       shows the key once a keyboard is in use: while the field is empty,
       and while it is unfocused even with text (searchHintShows). The
       pane and list variants draw it as plain 11 px text in label-3, as
       the boards and the app's ClSearchField do; home keeps the key cap. -->
  <label class="tsearch" :class="`tsearch--${variant}`">
    <svg class="tsearch-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" :stroke-width="variant === 'list' ? 2.3 : 2" stroke-linecap="round" aria-hidden="true">
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="M15.5 15.5 20 20" />
    </svg>
    <input
      ref="input"
      class="tsearch-input"
      type="search"
      :value="modelValue"
      :placeholder="placeholder"
      :aria-label="ariaLabel || placeholder"
      enterkeyhint="search"
      @input="$emit('update:modelValue', $event.target.value)"
      @keydown.enter.prevent="$emit('submit', modelValue)"
      @focus="onFocus"
      @blur="focused = false"
    />
    <span v-if="showChip" class="tsearch-chip" aria-hidden="true">{{ chipText }}</span>
  </label>
</template>

<script>
import { shellState } from "@/shell/shellState.js";
import { isMac, registerSearch, searchHintShows } from "@/shell/shortcuts.js";

export default {
  name: "ToolbarSearch",
  props: {
    modelValue: { type: String, default: "" },
    placeholder: { type: String, default: "Search" },
    // "⌘K", "⌘F" or none.
    hint: { type: String, default: "" },
    variant: {
      type: String,
      default: "pane",
      validator: (v) => ["home", "pane", "list", "phone"].includes(v),
    },
    ariaLabel: { type: String, default: "" },
  },
  emits: ["update:modelValue", "submit", "focus"],
  data: () => ({ focused: false }),
  computed: {
    showChip() {
      return searchHintShows({
        hint: this.hint,
        text: this.modelValue,
        focused: this.focused,
        keyboardUsed: shellState.keyboardUsed,
        variant: this.variant,
      });
    },
    chipText() {
      return isMac() ? this.hint : this.hint.replace("⌘", "Ctrl ");
    },
  },
  mounted() {
    if (this.hint) {
      this.unregister = registerSearch(this.$refs.input, { kind: /F$/i.test(this.hint) ? "f" : "k" });
    }
  },
  beforeUnmount() {
    if (this.unregister) this.unregister();
  },
  methods: {
    focus() {
      this.$refs.input?.focus();
    },
    onFocus(e) {
      this.focused = true;
      this.$emit("focus", e);
    },
  },
};
</script>

<style scoped>
.tsearch {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 8px;
  border-radius: 8px;
  background: var(--cl-control-fill-pointer);
  color: var(--cl-label-50);
  box-sizing: border-box;
  min-width: 0;
  cursor: text;
}
.tsearch--home {
  width: 100%;
  max-width: 460px;
  margin: 0 auto;
}
.tsearch--pane {
  width: 340px;
  max-width: 100%;
}
.tsearch--list {
  height: 28px;
  border-radius: 7px;
  padding: 0 8px;
}
.tsearch--phone {
  height: 36px;
  border-radius: 10px;
  padding: 0 8px;
  background: var(--cl-control-fill);
  gap: 6px;
}
.tsearch-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}
.tsearch--list .tsearch-icon {
  width: 13px;
  height: 13px;
}
.tsearch--phone .tsearch-icon {
  width: 18px;
  height: 18px;
}
.tsearch-input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  color: var(--cl-label);
  font: 400 13px/16px var(--cl-font);
  padding: 0;
}
.tsearch--phone .tsearch-input {
  font: 400 17px/22px var(--cl-font);
}
.tsearch-input::placeholder {
  color: var(--cl-label-50);
}
.tsearch-input::-webkit-search-cancel-button {
  -webkit-appearance: none;
}
.tsearch-chip {
  flex-shrink: 0;
  padding: 1px 5px;
  border-radius: 4px;
  border: 1px solid var(--cl-border-default);
  color: var(--cl-label-3);
  font: 400 11px/14px var(--cl-font-mono);
}
.tsearch--pane .tsearch-chip,
.tsearch--list .tsearch-chip {
  border: none;
  padding: 0;
  font: 400 11px/14px var(--cl-font);
  color: var(--cl-label-3);
}
</style>
